/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import useRouter from 'js/lib/useRouter';

import { Button, Dialog, InlineNotification, Typography2, breakpoints } from '@coursera/cds-core';

import { resendInviteLink } from 'bundles/saml-sso/utils/JWTLoginAPIUtils';

import _t from 'i18n!nls/saml-sso';

type ResendJwtInviteLinkProps = {
  onClose: () => void;
};

const styles = {
  status: css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-300);
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  actionsWrapper: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-300);
    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  actionButton: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: none;
    }
  `,
};

type ExpiredInviteLinkPromptProps = {
  focusRef: React.RefObject<HTMLDivElement>;
  isSending: boolean;
  onSendNewLink: () => void;
  onCancel: () => void;
};

const ExpiredInviteLinkPrompt = ({ focusRef, isSending, onSendNewLink, onCancel }: ExpiredInviteLinkPromptProps) => (
  <React.Fragment>
    <Dialog.HeadingGroup ref={focusRef} tabIndex={-1}>
      {_t('This link has expired')}
    </Dialog.HeadingGroup>
    <Dialog.Content>
      <div role="status" css={styles.status}>
        <Typography2 component="p" variant="bodyPrimary">
          {_t('This sign-in link is no longer available. We can send you a new one so you can continue to Coursera.')}
        </Typography2>
      </div>
    </Dialog.Content>
    <Dialog.Actions css={styles.actionsWrapper}>
      <Button css={styles.actionButton} size="medium" variant="primary" onClick={onSendNewLink} disabled={isSending}>
        {_t('Send a new link')}
      </Button>
      <Button css={styles.actionButton} size="medium" variant="ghost" onClick={onCancel}>
        {_t('Cancel')}
      </Button>
    </Dialog.Actions>
  </React.Fragment>
);

type NewInviteLinkSentConfirmationProps = {
  focusRef: React.RefObject<HTMLDivElement>;
  onDone: () => void;
};

const NewInviteLinkSentConfirmation = ({ focusRef, onDone }: NewInviteLinkSentConfirmationProps) => (
  <React.Fragment>
    <Dialog.HeadingGroup tabIndex={-1}>{_t('Your link is on the way')}</Dialog.HeadingGroup>
    <Dialog.Content>
      <div role="status" css={styles.status}>
        <Typography2 component="p" variant="bodyPrimary">
          {_t("We'll send a new sign-in link to your email. It may take a few minutes to arrive in your inbox.")}
        </Typography2>
        <InlineNotification severity="information">
          {_t("If you don't receive it within 10 minutes, please contact your organization's admin for help.")}
        </InlineNotification>
      </div>
    </Dialog.Content>
    <Dialog.Actions css={styles.actionsWrapper}>
      <Button variant="primary" onClick={onDone}>
        {_t('Done')}
      </Button>
    </Dialog.Actions>
  </React.Fragment>
);

const ResendJwtInviteLink = ({ onClose }: ResendJwtInviteLinkProps) => {
  const router = useRouter();
  const [isSent, setIsSent] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const focusRef = React.useRef<HTMLDivElement>(null);

  // The primary CTA is removed once sent, so move focus to the heading that replaces it rather
  // than dropping the learner back at the top of the dialog.
  React.useEffect(() => {
    if (isSent) {
      focusRef.current?.focus();
    }
  }, [isSent]);

  const handleSendNewLink = async () => {
    if (isSending) return;

    setIsSending(true);
    try {
      await resendInviteLink(router);
    } catch {
      // The learner sees the same "on the way" confirmation regardless of outcome, so failures are
      // swallowed here rather than surfaced.
    } finally {
      setIsSending(false);
      setIsSent(true);
    }
  };

  return (
    <Dialog
      alignment="start"
      aria-describedby="dialog-content"
      open
      onClose={onClose}
      width="medium"
      variant="standard"
    >
      {isSent ? (
        <NewInviteLinkSentConfirmation focusRef={focusRef} onDone={onClose} />
      ) : (
        <ExpiredInviteLinkPrompt
          focusRef={focusRef}
          isSending={isSending}
          onSendNewLink={handleSendNewLink}
          onCancel={onClose}
        />
      )}
    </Dialog>
  );
};

export default ResendJwtInviteLink;
